import React, { useState, useEffect, useCallback } from 'react';
import { getSwingPositions, updateSwingPosition, deleteSwingPosition, getSwingLivePrices } from '../../api/client';
import useMediaQuery from '../../hooks/useMediaQuery';
import MobileCard, { MobileCardHeader, MobileCardBody, MobileCardActions, MobileKV } from './MobileCard';

const STATUS_TABS = ['ACTIVE', 'WATCHLIST', 'EXITED'];

function rowBgColor(pos, ltp) {
  if (pos.status === 'EXITED') return 'transparent';
  const price = ltp || null;
  if (!price || !pos.entry_price) return 'transparent';
  if (pos.stop_loss && price <= pos.stop_loss) return 'rgba(244,67,54,0.07)';
  if (price >= pos.entry_price) return 'rgba(76,175,80,0.05)';
  return 'rgba(255,152,0,0.05)';
}

function InlineEdit({ label, value, onSave, type = 'number', prefix = '₹' }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState(value || '');

  const commit = async () => {
    const parsed = type === 'number' ? parseFloat(val) : val;
    if (type === 'number' && isNaN(parsed)) { setEditing(false); return; }
    await onSave(parsed);
    setEditing(false);
  };

  if (editing) {
    return (
      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        <input
          autoFocus
          type={type}
          value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
          style={{ width: 80, padding: '2px 6px', background: '#1e1e1e', border: '1px solid #4CAF50', color: '#fff', borderRadius: 4, fontSize: 12 }}
        />
        <button onClick={commit} style={{ background: '#4CAF50', border: 'none', color: '#fff', borderRadius: 3, padding: '2px 6px', cursor: 'pointer', fontSize: 11 }}>✓</button>
        <button onClick={() => setEditing(false)} style={{ background: '#333', border: 'none', color: '#aaa', borderRadius: 3, padding: '2px 6px', cursor: 'pointer', fontSize: 11 }}>✕</button>
      </span>
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      title={`Click to edit ${label}`}
      style={{ cursor: 'pointer', borderBottom: '1px dashed #444', color: value ? '#fff' : '#555' }}
    >
      {value ? (type === 'number' ? `${prefix}${parseFloat(value).toLocaleString('en-IN')}` : value) : '—'}
    </span>
  );
}

function PnlCell({ pos, ltp }) {
  if (!ltp || !pos.entry_price || !pos.quantity) {
    return <span style={{ color: '#555', fontSize: 12 }}>—</span>;
  }
  const unrealised = (ltp - pos.entry_price) * pos.quantity;
  const pct        = ((ltp - pos.entry_price) / pos.entry_price) * 100;
  const color      = unrealised >= 0 ? '#4CAF50' : '#f44336';
  const sign       = unrealised >= 0 ? '+' : '';
  return (
    <div style={{ fontSize: 12 }}>
      <div style={{ color, fontWeight: 600 }}>
        {sign}₹{Math.round(Math.abs(unrealised)).toLocaleString('en-IN')}
      </div>
      <div style={{ color, opacity: 0.8, fontSize: 11 }}>
        {sign}{pct.toFixed(2)}%
      </div>
    </div>
  );
}

/**
 * PositionCardMobile — mobile-friendly card layout for a single SwingPosition.
 * Used in place of PositionRow when the viewport is narrow. Same actions
 * available; just stacked vertically with bigger tap targets.
 */
function PositionCardMobile({ pos, ltp, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [exitMode, setExitMode] = useState(false);
  const [exitPrice, setExitPrice] = useState('');
  const [activateMode, setActivateMode] = useState(false);
  const [activatePrice, setActivatePrice] = useState(pos.entry_price > 0 ? pos.entry_price : '');
  const [activateQty, setActivateQty]   = useState(pos.quantity > 0 ? pos.quantity : '');

  const update = async (data) => {
    setBusy(true);
    try { await updateSwingPosition(pos.id, data); onRefresh(); } catch {}
    finally { setBusy(false); }
  };
  const handleActivate = async () => {
    const ep = parseFloat(activatePrice);
    const qty = parseInt(activateQty);
    if (isNaN(ep) || ep <= 0 || isNaN(qty) || qty <= 0) return;
    await update({ status: 'ACTIVE', entry_price: ep, quantity: qty });
    setActivateMode(false);
  };
  const handleExit = async () => {
    const ep = parseFloat(exitPrice);
    if (isNaN(ep) || ep <= 0) return;
    await update({ status: 'EXITED', exit_price: ep });
    setExitMode(false);
  };
  const handleDelete = async () => {
    if (!window.confirm(`Remove ${pos.symbol} from portfolio?`)) return;
    setBusy(true);
    try { await deleteSwingPosition(pos.id); onRefresh(); } finally { setBusy(false); }
  };

  // Compute P&L for the card
  let pnlValue = null;
  let pnlPct = null;
  if (pos.status === 'EXITED' && pos.realised_pnl != null) {
    pnlValue = pos.realised_pnl;
    pnlPct = pos.pnl_pct;
  } else if (ltp && pos.entry_price && pos.quantity) {
    pnlValue = (ltp - pos.entry_price) * pos.quantity;
    pnlPct = ((ltp - pos.entry_price) / pos.entry_price) * 100;
  }
  const pnlColor = pnlValue == null ? '#888' : pnlValue >= 0 ? '#4CAF50' : '#f44';

  // Card accent color based on status
  const statusColor = pos.status === 'ACTIVE' ? '#4CAF50'
                    : pos.status === 'WATCHLIST' ? '#2196F3'
                    : '#888';

  const statusBadge = (
    <span style={{
      padding: '3px 10px', borderRadius: 99, fontSize: 10, fontWeight: 700,
      background: statusColor + '22', color: statusColor, border: `1px solid ${statusColor}66`,
      letterSpacing: '0.05em',
    }}>
      {pos.status}
    </span>
  );

  return (
    <MobileCard accent={statusColor} style={{ opacity: busy ? 0.6 : 1 }}>
      <MobileCardHeader symbol={pos.symbol} sector={pos.sector} right={statusBadge} />

      <MobileCardBody cols={2}>
        <MobileKV label="Entry ₹" mono bold
          value={pos.entry_price ? `₹${parseFloat(pos.entry_price).toLocaleString('en-IN')}` : null} />
        <MobileKV label="Qty"
          value={pos.quantity > 0 ? pos.quantity : null} />

        {pos.status !== 'EXITED' && (
          <>
            <MobileKV label="LTP ₹" mono bold
              color={ltp ? (ltp >= (pos.entry_price || 0) ? '#4CAF50' : '#FF9800') : '#555'}
              value={ltp ? `₹${parseFloat(ltp).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : 'tap refresh'} />
            <MobileKV label="Unreal. P&L" mono bold color={pnlColor}
              value={pnlValue != null ? `${pnlValue >= 0 ? '+' : ''}₹${Math.round(Math.abs(pnlValue)).toLocaleString('en-IN')}${pnlPct != null ? ` (${pnlPct >= 0 ? '+' : ''}${pnlPct.toFixed(1)}%)` : ''}` : null} />
          </>
        )}

        {pos.status === 'EXITED' && (
          <>
            <MobileKV label="Exit ₹" mono bold
              value={pos.exit_price ? `₹${parseFloat(pos.exit_price).toLocaleString('en-IN')}` : null} />
            <MobileKV label="Realised P&L" mono bold color={pnlColor}
              value={pnlValue != null ? `${pnlValue >= 0 ? '+' : ''}₹${Math.round(Math.abs(pnlValue)).toLocaleString('en-IN')}${pnlPct != null ? ` (${pnlPct >= 0 ? '+' : ''}${pnlPct.toFixed(1)}%)` : ''}` : '—'} />
          </>
        )}

        <MobileKV label="SL ₹" mono color="#f44"
          value={pos.stop_loss ? `₹${parseFloat(pos.stop_loss).toLocaleString('en-IN')}` : null} />
        <MobileKV label="T1 ₹" mono color="#4CAF50"
          value={pos.target1 ? `₹${parseFloat(pos.target1).toLocaleString('en-IN')}` : null} />
        <MobileKV label="T2 ₹" mono color="#4CAF50"
          value={pos.target2 ? `₹${parseFloat(pos.target2).toLocaleString('en-IN')}` : null} />
        <MobileKV label="Entry Date"
          value={pos.entry_date ? new Date(pos.entry_date).toLocaleDateString('en-IN') : null} />
      </MobileCardBody>

      {/* Inline activate / exit forms */}
      {activateMode && (
        <div style={{ marginTop: 12, padding: 10, background: '#0d1f0d', border: '1px solid #2d6a2d', borderRadius: 6 }}>
          <div style={{ fontSize: 11, color: '#4CAF50', fontWeight: 700, marginBottom: 8 }}>ACTIVATE POSITION</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <input type="number" value={activatePrice} onChange={e => setActivatePrice(e.target.value)} placeholder="Entry ₹"
              style={{ flex: 1, padding: '10px 12px', background: '#1e1e1e', border: '1px solid #4CAF50', color: '#fff', borderRadius: 6, fontSize: 14 }} />
            <input type="number" value={activateQty} onChange={e => setActivateQty(e.target.value)} placeholder="Qty"
              style={{ width: 80, padding: '10px 12px', background: '#1e1e1e', border: '1px solid #4CAF50', color: '#fff', borderRadius: 6, fontSize: 14 }} />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={handleActivate} style={{ flex: 1, padding: '10px', background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13 }}>Confirm</button>
            <button onClick={() => setActivateMode(false)} style={{ padding: '10px 16px', background: '#333', color: '#aaa', border: 'none', borderRadius: 6 }}>Cancel</button>
          </div>
        </div>
      )}
      {exitMode && (
        <div style={{ marginTop: 12, padding: 10, background: '#1a1a1a', border: '1px solid #FF9800', borderRadius: 6 }}>
          <div style={{ fontSize: 11, color: '#FF9800', fontWeight: 700, marginBottom: 8 }}>MARK EXITED</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <input type="number" value={exitPrice} onChange={e => setExitPrice(e.target.value)} placeholder="Exit ₹"
              style={{ flex: 1, padding: '10px 12px', background: '#1e1e1e', border: '1px solid #444', color: '#fff', borderRadius: 6, fontSize: 14 }} />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={handleExit} style={{ flex: 1, padding: '10px', background: '#FF9800', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13 }}>Mark Exited</button>
            <button onClick={() => setExitMode(false)} style={{ padding: '10px 16px', background: '#333', color: '#aaa', border: 'none', borderRadius: 6 }}>Cancel</button>
          </div>
        </div>
      )}

      <MobileCardActions>
        {pos.status === 'WATCHLIST' && !activateMode && (
          <button
            onClick={() => { setActivateMode(true); setExitMode(false); }}
            style={{ flex: 1, minHeight: 40, padding: '8px', background: '#0a2010', border: '1px solid #2d6a2d', color: '#4CAF50', borderRadius: 6, fontWeight: 700, fontSize: 13 }}
          >
            ▶ Activate
          </button>
        )}
        {pos.status !== 'EXITED' && !exitMode && (
          <button
            onClick={() => { setExitMode(true); setActivateMode(false); }}
            style={{ minHeight: 40, padding: '8px 14px', background: '#1e1e1e', border: '1px solid #444', color: '#FF9800', borderRadius: 6, fontWeight: 600, fontSize: 13 }}
          >
            Exit
          </button>
        )}
        <button
          onClick={handleDelete}
          disabled={busy}
          style={{ minHeight: 40, padding: '8px 14px', background: '#2a1010', border: '1px solid #5a2020', color: '#f44', borderRadius: 6, fontSize: 13 }}
        >
          ✕
        </button>
      </MobileCardActions>
    </MobileCard>
  );
}

function PositionRow({ pos, ltp, onRefresh }) {
  const [busy, setBusy]           = useState(false);
  const [noteMode, setNoteMode]   = useState(false);
  const [note, setNote]           = useState(pos.notes || '');
  const [exitPrice, setExitPrice] = useState('');
  const [exitMode, setExitMode]   = useState(false);
  const [activateMode, setActivateMode] = useState(false);
  const [activatePrice, setActivatePrice] = useState(pos.entry_price > 0 ? pos.entry_price : '');
  const [activateQty, setActivateQty]   = useState(pos.quantity > 0 ? pos.quantity : '');

  const update = async (data) => {
    setBusy(true);
    try { await updateSwingPosition(pos.id, data); onRefresh(); }
    catch { /* silent */ }
    finally { setBusy(false); }
  };

  const handleActivate = async () => {
    const ep = parseFloat(activatePrice);
    const qty = parseInt(activateQty);
    if (isNaN(ep) || ep <= 0 || isNaN(qty) || qty <= 0) return;
    await update({ status: 'ACTIVE', entry_price: ep, quantity: qty });
    setActivateMode(false);
  };

  const handleMarkExited = async () => {
    const ep = parseFloat(exitPrice);
    if (isNaN(ep) || ep <= 0) return;
    await update({ status: 'EXITED', exit_price: ep });
    setExitMode(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove ${pos.symbol} from portfolio?`)) return;
    setBusy(true);
    try { await deleteSwingPosition(pos.id); onRefresh(); }
    finally { setBusy(false); }
  };

  const pnlColor = pos.realised_pnl > 0 ? '#4CAF50' : pos.realised_pnl < 0 ? '#f44' : '#aaa';
  const ltpColor = ltp
    ? (pos.stop_loss && ltp <= pos.stop_loss ? '#f44' : ltp >= (pos.entry_price || 0) ? '#4CAF50' : '#FF9800')
    : '#555';

  return (
    <>
      <tr style={{ borderBottom: '1px solid #222', background: rowBgColor(pos, ltp), opacity: busy ? 0.6 : 1 }}>
        <td style={{ padding: '10px 12px', fontWeight: 700, fontFamily: 'monospace', fontSize: 13 }}>{pos.symbol}</td>
        <td style={{ padding: '10px 12px', color: '#888', fontSize: 12 }}>{pos.sector || '—'}</td>
        <td style={{ padding: '10px 12px', fontSize: 12, color: '#888' }}>
          {pos.entry_date ? new Date(pos.entry_date).toLocaleDateString('en-IN') : '—'}
        </td>
        {/* Entry ₹ — editable on non-EXITED rows so the actual broker fill price can be recorded */}
        <td style={{ padding: '10px 12px', fontVariantNumeric: 'tabular-nums' }}>
          {pos.status === 'EXITED'
            ? (pos.entry_price ? `₹${parseFloat(pos.entry_price).toLocaleString('en-IN')}` : '—')
            : <InlineEdit label="Entry ₹" value={pos.entry_price} onSave={v => update({ entry_price: v })} />
          }
        </td>
        {/* Qty — editable on non-EXITED rows for the same reason */}
        <td style={{ padding: '10px 12px' }}>
          {pos.status === 'EXITED'
            ? (pos.quantity > 0 ? pos.quantity : <span style={{ color: '#555' }}>—</span>)
            : <InlineEdit label="Qty" value={pos.quantity} onSave={v => update({ quantity: parseInt(v) || 0 })} prefix="" />
          }
        </td>
        {/* Live price — shown for ACTIVE and WATCHLIST (the latter helps spot entries triggering) */}
        <td style={{ padding: '10px 12px', fontVariantNumeric: 'tabular-nums' }}>
          {pos.status !== 'EXITED'
            ? ltp
              ? <span style={{ color: ltpColor, fontWeight: 600 }}>₹{parseFloat(ltp).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              : <span style={{ color: '#444', fontSize: 11 }}>—</span>
            : <span style={{ color: '#333', fontSize: 11 }}>—</span>
          }
        </td>
        {/* P&L — Unrealised for ACTIVE+WATCHLIST (notional for watchlist), Realised for EXITED */}
        <td style={{ padding: '10px 12px' }}>
          {pos.status === 'EXITED'
            ? (pos.realised_pnl !== undefined
                ? <span style={{ color: pnlColor, fontSize: 12, fontWeight: 600 }}>
                    {pos.realised_pnl >= 0 ? '+' : ''}₹{Math.round(pos.realised_pnl).toLocaleString('en-IN')}
                    {pos.pnl_pct != null && <span style={{ fontSize: 10, marginLeft: 4 }}>({pos.pnl_pct >= 0 ? '+' : ''}{pos.pnl_pct?.toFixed(1)}%)</span>}
                  </span>
                : <span style={{ color: '#555', fontSize: 12 }}>—</span>
              )
            : <PnlCell pos={pos} ltp={ltp} />
          }
        </td>
        <td style={{ padding: '10px 12px', color: '#f44' }}>
          <InlineEdit label="SL" value={pos.stop_loss} onSave={v => update({ stop_loss: v })} />
        </td>
        <td style={{ padding: '10px 12px', color: '#4CAF50' }}>
          <InlineEdit label="T1" value={pos.target1} onSave={v => update({ target1: v })} />
        </td>
        <td style={{ padding: '10px 12px', color: '#4CAF50' }}>
          <InlineEdit label="T2" value={pos.target2} onSave={v => update({ target2: v })} />
        </td>
        <td style={{ padding: '10px 12px' }}>
          <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: pos.status === 'ACTIVE' ? '#0a2010' : pos.status === 'WATCHLIST' ? '#1a1a2a' : '#1a1a1a', color: pos.status === 'ACTIVE' ? '#4CAF50' : pos.status === 'WATCHLIST' ? '#2196F3' : '#888', border: `1px solid ${pos.status === 'ACTIVE' ? '#2d6a2d' : pos.status === 'WATCHLIST' ? '#1e4a7a' : '#333'}` }}>
            {pos.status}
          </span>
        </td>
        <td style={{ padding: '10px 12px' }}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {(pos.tags || []).map(t => (
              <span key={t} style={{ padding: '2px 6px', borderRadius: 99, fontSize: 10, background: '#2a2a2a', color: '#aaa', border: '1px solid #444' }}>{t}</span>
            ))}
          </div>
        </td>
        <td style={{ padding: '10px 12px', fontSize: 12, color: '#888', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {pos.notes || <span style={{ color: '#444' }}>—</span>}
        </td>
        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'nowrap' }}>
            {pos.status === 'WATCHLIST' && (
              <button
                onClick={() => { setActivateMode(m => !m); setExitMode(false); setNoteMode(false); }}
                title="Activate position"
                style={{ padding: '3px 8px', fontSize: 11, background: '#0a2010', border: '1px solid #2d6a2d', color: '#4CAF50', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
              >
                ▶
              </button>
            )}
            {pos.status !== 'EXITED' && (
              <>
                <button
                  onClick={() => { setNoteMode(m => !m); setActivateMode(false); setExitMode(false); }}
                  title="Add note"
                  style={{ padding: '3px 7px', fontSize: 12, background: '#1e1e1e', border: '1px solid #444', color: '#aaa', borderRadius: 4, cursor: 'pointer' }}
                >
                  📝
                </button>
                <button
                  onClick={() => { setExitMode(m => !m); setActivateMode(false); setNoteMode(false); }}
                  title="Record exit"
                  style={{ padding: '3px 8px', fontSize: 11, background: '#1e1e1e', border: '1px solid #444', color: '#FF9800', borderRadius: 4, cursor: 'pointer' }}
                >
                  Exit
                </button>
              </>
            )}
            <button
              onClick={handleDelete}
              disabled={busy}
              title="Remove position"
              style={{ padding: '3px 7px', fontSize: 11, background: '#2a1010', border: '1px solid #5a2020', color: '#f44', borderRadius: 4, cursor: 'pointer' }}
            >
              ✕
            </button>
          </div>
        </td>
      </tr>
      {(activateMode || noteMode || exitMode) && (
        <tr>
          <td colSpan={14} style={{ padding: '0 12px 12px 12px', background: '#141414' }}>
            {activateMode && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingTop: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: '#4CAF50', fontWeight: 600 }}>Activate Position</span>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <label style={{ fontSize: 12, color: '#888' }}>Entry ₹</label>
                  <input
                    type="number" value={activatePrice} onChange={e => setActivatePrice(e.target.value)}
                    placeholder="e.g. 1250"
                    style={{ width: 110, padding: '6px 10px', background: '#1e1e1e', border: '1px solid #4CAF50', color: '#fff', borderRadius: 6, fontSize: 12 }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <label style={{ fontSize: 12, color: '#888' }}>Qty</label>
                  <input
                    type="number" value={activateQty} onChange={e => setActivateQty(e.target.value)}
                    placeholder="e.g. 50"
                    style={{ width: 80, padding: '6px 10px', background: '#1e1e1e', border: '1px solid #4CAF50', color: '#fff', borderRadius: 6, fontSize: 12 }}
                  />
                </div>
                <button
                  onClick={handleActivate}
                  style={{ padding: '6px 16px', background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                >
                  Confirm Entry
                </button>
                <button
                  onClick={() => setActivateMode(false)}
                  style={{ padding: '6px 14px', background: '#333', color: '#aaa', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                >
                  Cancel
                </button>
              </div>
            )}
            {noteMode && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', paddingTop: 8 }}>
                <textarea
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="Add a note..."
                  style={{ flex: 1, padding: '6px 10px', background: '#1e1e1e', border: '1px solid #444', color: '#fff', borderRadius: 6, fontSize: 12, resize: 'vertical', minHeight: 60 }}
                />
                <button
                  onClick={() => { update({ notes: note }); setNoteMode(false); }}
                  style={{ padding: '6px 14px', background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                >
                  Save
                </button>
                <button
                  onClick={() => setNoteMode(false)}
                  style={{ padding: '6px 14px', background: '#333', color: '#aaa', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                >
                  Cancel
                </button>
              </div>
            )}
            {exitMode && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingTop: 8 }}>
                <label style={{ fontSize: 12, color: '#888' }}>Exit Price (₹)</label>
                <input
                  type="number"
                  value={exitPrice}
                  onChange={e => setExitPrice(e.target.value)}
                  placeholder="e.g. 2450"
                  style={{ width: 140, padding: '6px 10px', background: '#1e1e1e', border: '1px solid #444', color: '#fff', borderRadius: 6, fontSize: 12 }}
                />
                <button
                  onClick={handleMarkExited}
                  style={{ padding: '6px 14px', background: '#FF9800', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                >
                  Mark Exited
                </button>
                <button
                  onClick={() => setExitMode(false)}
                  style={{ padding: '6px 14px', background: '#333', color: '#aaa', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                >
                  Cancel
                </button>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// Cache key for sessionStorage — survives tab switches & navigation,
// cleared on browser close. 15-minute TTL (prices stale after that).
const LTP_CACHE_KEY = 'swing_live_prices_v1';
const LTP_CACHE_TTL_MS = 15 * 60 * 1000;

function _loadCachedPrices() {
  try {
    const raw = sessionStorage.getItem(LTP_CACHE_KEY);
    if (!raw) return { prices: {}, refreshedAt: null };
    const { prices, refreshedAt } = JSON.parse(raw);
    if (!refreshedAt) return { prices: {}, refreshedAt: null };
    const age = Date.now() - new Date(refreshedAt).getTime();
    if (age > LTP_CACHE_TTL_MS) return { prices: {}, refreshedAt: null };
    return { prices: prices || {}, refreshedAt };
  } catch {
    return { prices: {}, refreshedAt: null };
  }
}

function _saveCachedPrices(prices, refreshedAt) {
  try {
    sessionStorage.setItem(LTP_CACHE_KEY, JSON.stringify({ prices, refreshedAt }));
  } catch { /* quota exceeded or disabled — non-fatal */ }
}

export default function SwingPortfolio() {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [positions, setPositions]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [statusTab, setStatusTab]   = useState('ACTIVE');
  const [error, setError]           = useState('');
  // Initialise LTP state from sessionStorage so prices survive component
  // unmount (tab navigation) and remount. Previously every visit started
  // with empty livePrices and forced a fresh refresh click.
  const _initial = _loadCachedPrices();
  const [livePrices, setLivePrices] = useState(_initial.prices);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(_initial.refreshedAt);

  const load = useCallback(async () => {
    setLoading(true);
    // DON'T clear livePrices here — they're a separate concern from the
    // positions list. Stale entries for symbols not in the current tab
    // are harmless; valid entries shared with another tab survive.
    try {
      const res = await getSwingPositions({ status: statusTab });
      setPositions(res.data.positions || []);
    } catch {
      setError('Failed to load positions');
    } finally {
      setLoading(false);
    }
  }, [statusTab]);

  useEffect(() => { load(); }, [load]);

  // Persist livePrices to sessionStorage whenever they change
  useEffect(() => {
    if (Object.keys(livePrices).length > 0 && refreshedAt) {
      _saveCachedPrices(livePrices, refreshedAt);
    }
  }, [livePrices, refreshedAt]);

  const handleRefreshPrices = async () => {
    // Refresh works for ACTIVE and WATCHLIST. Skipped for EXITED (no LTP needed).
    const eligible = positions.filter(
      p => (p.status === 'ACTIVE' || p.status === 'WATCHLIST') && p.symbol
    );
    if (!eligible.length) return;
    setRefreshing(true);
    setError('');
    try {
      const syms = eligible.map(p => p.symbol);
      const res  = await getSwingLivePrices(syms);
      // Merge with existing cache so prices for the OTHER tab persist too
      setLivePrices(prev => ({ ...prev, ...(res.data.prices || {}) }));
      setRefreshedAt(res.data.refreshed_at);
    } catch {
      setError('Failed to fetch live prices — yfinance may be temporarily unavailable');
    } finally {
      setRefreshing(false);
    }
  };

  // Aggregate unrealised P&L across positions in the current tab with live prices.
  // For WATCHLIST this only counts entries with qty > 0 (i.e. those added from
  // Generated Portfolio with suggested qty).
  const totalUnrealised = positions
    .filter(p =>
      (p.status === 'ACTIVE' || p.status === 'WATCHLIST')
      && livePrices[p.symbol] && p.entry_price && p.quantity
    )
    .reduce((sum, p) => sum + (livePrices[p.symbol] - p.entry_price) * p.quantity, 0);
  const hasPrices = Object.keys(livePrices).length > 0;

  // Cache freshness indicator
  const cacheAgeMs = refreshedAt ? Date.now() - new Date(refreshedAt).getTime() : null;
  const isStale = cacheAgeMs != null && cacheAgeMs > 5 * 60 * 1000;

  const HEADERS_BY_TAB = {
    ACTIVE:    ['Symbol', 'Sector', 'Entry Date', 'Entry ₹', 'Qty', 'LTP ₹', 'Unreal. P&L', 'SL ₹', 'T1 ₹', 'T2 ₹', 'Status', 'Tags', 'Notes', 'Actions'],
    WATCHLIST: ['Symbol', 'Sector', 'Entry Date', 'Entry ₹', 'Qty', 'LTP ₹', 'Unreal. P&L', 'SL ₹', 'T1 ₹', 'T2 ₹', 'Status', 'Tags', 'Notes', 'Actions'],
    EXITED:    ['Symbol', 'Sector', 'Entry Date', 'Entry ₹', 'Qty', 'LTP ₹', 'Realised P&L', 'SL ₹', 'T1 ₹', 'T2 ₹', 'Status', 'Tags', 'Notes', 'Actions'],
  };
  const HEADERS = HEADERS_BY_TAB[statusTab] || HEADERS_BY_TAB.ACTIVE;

  return (
    <div>
      {error && <div style={{ color: '#f44', marginBottom: 12, padding: '8px 12px', background: '#2a1010', borderRadius: 6 }}>{error}</div>}

      {/* Status tabs + refresh */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {STATUS_TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setStatusTab(tab)}
            style={{
              padding: '6px 16px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
              background: statusTab === tab ? '#4CAF50' : '#1e1e1e',
              color: statusTab === tab ? '#fff' : '#aaa',
              border: statusTab === tab ? '1px solid #4CAF50' : '1px solid #444',
              fontWeight: statusTab === tab ? 600 : 400,
            }}
          >
            {tab}
          </button>
        ))}
        {/* Refresh enabled for both ACTIVE and WATCHLIST — both benefit from live prices */}
        {(statusTab === 'ACTIVE' || statusTab === 'WATCHLIST') && (
          <button
            onClick={handleRefreshPrices}
            disabled={refreshing || positions.length === 0}
            title={statusTab === 'WATCHLIST'
              ? 'Fetch live prices for all WATCHLIST symbols (helps spot entries triggering)'
              : 'Fetch live prices for all ACTIVE positions'
            }
            style={{
              padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: refreshing ? 'not-allowed' : 'pointer',
              background: refreshing ? '#1a2a1a' : (isStale ? '#3a2a0a' : '#0d1f0d'),
              color: refreshing ? '#666' : (isStale ? '#FFC107' : '#4CAF50'),
              border: `1px solid ${isStale ? '#7a5a00' : '#2d6a2d'}`,
              fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5,
            }}
          >
            {refreshing ? '⏳ Fetching...' : (isStale ? '⚠ Refresh Prices (stale)' : '🔄 Refresh Prices')}
          </button>
        )}
        <span style={{ marginLeft: 'auto', color: '#888', fontSize: 13, alignSelf: 'center', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {(statusTab === 'ACTIVE' || statusTab === 'WATCHLIST') && hasPrices && totalUnrealised !== 0 && (
            <span style={{ color: totalUnrealised >= 0 ? '#4CAF50' : '#f44', fontWeight: 600 }}>
              {statusTab === 'WATCHLIST' ? 'Notional P&L' : 'Unreal. P&L'}: {totalUnrealised >= 0 ? '+' : ''}₹{Math.round(Math.abs(totalUnrealised)).toLocaleString('en-IN')}
            </span>
          )}
          {refreshedAt && (statusTab === 'ACTIVE' || statusTab === 'WATCHLIST') && (
            <span style={{ color: isStale ? '#FFC107' : '#555', fontSize: 11 }}>
              prices as of {new Date(refreshedAt).toLocaleTimeString('en-IN')}
              {cacheAgeMs != null && cacheAgeMs > 60000 && (
                <span style={{ marginLeft: 4, opacity: 0.7 }}>
                  ({Math.round(cacheAgeMs / 60000)} min ago)
                </span>
              )}
            </span>
          )}
          <span>{positions.length} position{positions.length !== 1 ? 's' : ''}</span>
        </span>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 24, color: '#888', textAlign: 'center' }}>Loading...</div>
        ) : positions.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#666' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>💼</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>No {statusTab.toLowerCase()} positions</div>
            <div style={{ fontSize: 13, color: '#555' }}>
              {statusTab === 'ACTIVE'
                ? 'Use the Scanner tab to find candidates, then add them here.'
                : statusTab === 'WATCHLIST'
                  ? 'Add stocks to watchlist from the Scanner tab.'
                  : 'Exited positions will appear here.'}
            </div>
          </div>
        ) : isMobile ? (
          // ── Mobile: card list (each position becomes a card) ───────────────
          <div style={{ padding: 10 }}>
            {positions.map(pos => (
              <PositionCardMobile
                key={pos.id}
                pos={pos}
                ltp={livePrices[pos.symbol] || null}
                onRefresh={load}
              />
            ))}
          </div>
        ) : (
          // ── Desktop: full table ────────────────────────────────────────────
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', minWidth: 1000, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #333', background: '#141414' }}>
                  {HEADERS.map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {positions.map(pos => (
                  <PositionRow key={pos.id} pos={pos} ltp={livePrices[pos.symbol] || null} onRefresh={load} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
